import { Directive, Input, SimpleChange, OnChanges, OnInit, PLATFORM_ID, ElementRef, ComponentRef, SimpleChanges, Inject } from "@angular/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { isPlatformBrowser } from "@angular/common";
import { OverlayRef, Overlay, ConnectedPosition } from '@angular/cdk/overlay';
import { LoaderComponent } from "./loader.component";
import { ComponentPortal } from "@angular/cdk/portal";

declare global {
    interface HTMLElement {
        checkVisibility(): boolean;
    }
}

@Directive({
    selector: '[loader]',
    exportAs: 'loader',
    host: {
        '[style.opacity]': 'opacity'
    }
})
export class LoaderDirective implements OnChanges, OnInit {
    @Input() loader: Observable<any> | null = null;
    @Input() loadingText: string | null = null;
    @Input() isHalfSize: boolean = false;
    @Input() color: string = '#FFF';
    private subscription: Subscription | null = null;
    private opcObserver: MutationObserver | null = null; 
    private visObserver: IntersectionObserver | null = null;
    private isVisible = new BehaviorSubject(false);
    private overlayRef: OverlayRef | null = null;
    private componentRef: ComponentRef<LoaderComponent> | null = null;

    private host: HTMLElement;
    private isBrowser: boolean;
    private opacity: string;

    constructor(
        elementRef: ElementRef<HTMLElement>, 
        @Inject(PLATFORM_ID) platformId: Object, 
        private overlay: Overlay
    ) {
        this.host = elementRef.nativeElement;
        this.isBrowser = isPlatformBrowser(platformId);
        this.opacity = this.isBrowser ? getComputedStyle(this.host).opacity : '1';
    }

    readonly position: ConnectedPosition[] = [
        { 'originX': 'start', originY: 'top', overlayX: 'start', overlayY: 'top' }
    ]


    private initLoader() {
        let destroyLoader = () => this.destroyLoader();

        // cancel previous subscription if there was one and hide the loader if it was displayed
        destroyLoader();

        if (this.loader) {
            // start the subscription if the loader is a new observable
            this.subscription = this.loader.subscribe(destroyLoader, destroyLoader, destroyLoader);

            // only display the loader if the host element is visible
            if(this.isVisible.getValue())
                this.showLoader();
        }
    }

    ngOnInit() { 
        this.initLoader();
        this.isVisible.subscribe(value => {
            if(value) {
                // if the loader is visible and the loader is still loading, then redisplay the loader
                if(this.subscription && !this.subscription.closed) {
                    this.initLoader(); // reinitialize the loader with a new subscription and subscription
                                       // it will continue the loader
                }
            }
            else {
                this.hideLoader();
            }
        })

        if(this.isBrowser) {
            // chech for visibility of the host element
            this.visObserver = new IntersectionObserver(([ entry ]) => {
                // entry.isInterceting would be false if display is none, visibility is hidden,
                // width is 0, height is 0, opacity is 0, when the element is completely behind 
                // another element, or when the element is scrolled out of the viewport.
                this.isVisible.next(entry.isIntersecting);
            }, {
                root: null,
                rootMargin: '0px',
                threshold: 0.1
            });
            this.visObserver.observe(this.host);


            // keep up with opactiy changes
            this.opcObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
                        this.opacity = getComputedStyle(this.host).opacity;
                    }
                });
            });

            this.opcObserver.observe(document.body, {
                attributes: true,
                attributeFilter: ['style', 'class'],
                subtree: true,
                childList: true
            });
        }
    }

    ngOnChanges(c: SimpleChanges) {
        let onChange = (property: string, fn: () => void) => {
            let value = c[property] as SimpleChange | undefined;
            if(value && !value.isFirstChange() && value.previousValue !== value.currentValue) {
                fn();
            }
        }
        
        onChange('loader', () => this.initLoader());
        
        onChange('loadingText', () => {
            if(this.componentRef) {
                this.componentRef.instance.loadingText = this.loadingText;
                this.componentRef.changeDetectorRef.detectChanges();
            }
        });

        onChange('isHalfSize', () => {
            if(this.componentRef) {
                this.componentRef.instance.isHalfSize = this.isHalfSize;
                this.componentRef.changeDetectorRef.detectChanges();
            }
        });

        onChange('color', () => {
            if(this.componentRef) {
                this.componentRef.instance.color = this.color;
                this.componentRef.changeDetectorRef.detectChanges();
            }
        });
    }

    showLoader() {
        if(!this.overlayRef) {
            this.overlayRef = this.overlay.create({
                hasBackdrop: false,
                positionStrategy: this.overlay.position()
                    .flexibleConnectedTo(this.host)
                    .withPositions(this.position)
                    .withFlexibleDimensions(false)
                    .withPush(false)
                    .withViewportMargin(8),
                scrollStrategy: this.overlay.scrollStrategies.reposition(),
                width: this.host.offsetWidth,
                height: this.host.offsetHeight
            });

            let portal = new ComponentPortal(LoaderComponent);
            this.componentRef = this.overlayRef.attach(portal);
            this.componentRef.instance.loadingText = this.loadingText;
            this.componentRef.instance.isHalfSize = this.isHalfSize;
            this.componentRef.instance.color = this.color;
            this.componentRef.changeDetectorRef.detectChanges();
        }
    }

    // simply hides the loader. used when the element's visibility status changes to false.
    private hideLoader() {
        this.overlayRef?.detach();
        this.overlayRef?.dispose();
        this.overlayRef = null;
        this.componentRef?.destroy();
        this.componentRef = null;
    }

    // removes the loader and the subscription to the observable. it will indicated that the current loader is done loading 
    private destroyLoader() {
        this.subscription?.unsubscribe();
        this.subscription = null;
        this.hideLoader();
    }

    ngOnDestroy() {
        this.destroyLoader();
        this.visObserver?.disconnect();
        this.visObserver = null;
        this.opcObserver?.disconnect();
        this.opcObserver = null;
    }
}